import { useCallback, useMemo, useState } from 'react';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { getRaceUnitName } from '../../constants/game';
import { calculateTravelTime } from '../../constants/attackCalculator';
import { computeModifiedOffense } from '../../utils/minimumWinningForce';
import { buildUnitPool, buildAttackMods } from '../../hooks/useWarRoomState';
import { validateArmyComposition } from '../../utils/attackValidation';
import { launchAttack } from '../../api/attack';
import { useToast } from '../../hooks/useToast';
import { AmbushSourceSelector } from './AmbushSourceSelector';
import { ConfirmAttackDialog } from './ConfirmAttackDialog';
import { Button } from '../ui/Button';
import type { Province } from '../../types/province';
import type { AmbushableAttack, ArmyComposition, AttackResult } from '../../types/attack';

// SF-96: mirrors Result.Message.AMBUSH_NOT_ELIGIBLE (shared/.../result/Result.java) — the backend
// has no structured error-code field on this response, only free-text `message`, so matching this
// exact string is the only way to distinguish "list went stale" from a generic failure client-side.
const AMBUSH_NOT_ELIGIBLE_MESSAGE = 'The selected attack no longer qualifies, or has already been ambushed.';

interface AmbushLaunchPanelProps {
  province: Province;
  worldId: string;
  provinceId: string;
  refreshTrigger: number;
  onRefreshTriggerBump: () => void;
  onAttackComplete: (result: AttackResult) => void;
  refresh: () => Promise<void>;
}

interface AmbushComposition {
  generals: number;
  offenseSpecialists: number;
  elites: number;
  warHorses: number;
  prisoners: number;
  soldiers: number;
  mercenaries: number;
  includeSoldiers: boolean;
  includeMercenaries: boolean;
}

// SF-183 Component 7: sources from the at-home-only unit pool (buildUnitPool, which itself
// degrades to the away-inclusive sibling on a missing DTO field — see its own contract), not
// province.population.* directly — the launch seed can no longer include units already committed
// to another attack.
function buildInitialComposition(province: Province): AmbushComposition {
  const pool = buildUnitPool(province, province.metadata.race);
  return {
    generals: 4,
    offenseSpecialists: pool.offenseSpecialists,
    elites: pool.elites,
    warHorses: Math.min(pool.warHorses, pool.offenseSpecialists + pool.elites),
    prisoners: pool.prisoners,
    soldiers: 0,
    mercenaries: 0,
    includeSoldiers: false,
    includeMercenaries: false,
  };
}

/**
 * SF-147 Component 8: Ambush's minimal, dedicated launch control — replaces the retired
 * Calculator tab's (already-inaccurate, per Component 8's finding) Ambush support. Standalone
 * manual composition form (no MWF solver/oversend — Ambush has neither), "Your OP" readout
 * (computable without target intel, same reasoning as Component 3's own-offense card), and an
 * explicit "what you can know" block instead of any outcome/win-lose prediction — Ambush's
 * restricted defense formula (`computeAmbushDefense`) has no client-exposed value today.
 */
export function AmbushLaunchPanel({
  province,
  worldId,
  provinceId,
  refreshTrigger,
  onRefreshTriggerBump,
  onAttackComplete,
  refresh,
}: AmbushLaunchPanelProps) {
  const { addToast } = useToast();
  const race = province.metadata.race;
  const offspecName = getRaceUnitName(race, 'offense');
  const eliteName = getRaceUnitName(race, 'elite');

  const [selectedAttack, setSelectedAttack] = useState<AmbushableAttack | null>(null);
  const [composition, setComposition] = useState<AmbushComposition>(() => buildInitialComposition(province));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const unitPool = useMemo(() => buildUnitPool(province, race), [province, race]);
  const attackMods = useMemo(() => buildAttackMods(province), [province]);

  const armyComposition: ArmyComposition = useMemo(() => ({
    generals: composition.generals,
    soldiers: composition.soldiers,
    offenseSpecialists: composition.offenseSpecialists,
    elites: composition.elites,
    warHorses: composition.warHorses,
    prisoners: composition.prisoners,
    mercenaries: composition.mercenaries,
  }), [composition]);

  // "Your OP" — computable without any target intel, same reasoning as War Room's own-offense
  // card (Component 3). Defaults to the full available army, not zero (buildInitialComposition).
  const attackOP = useMemo(
    () => computeModifiedOffense(armyComposition, unitPool, { ...attackMods, generals: composition.generals }),
    [armyComposition, unitPool, attackMods, composition.generals]
  );

  const travelTime = useMemo(() => {
    if (!selectedAttack) {
      return calculateTravelTime(province, province.kingdom.isAtWar);
    }
    const [tgtKingdom, tgtIsland] = selectedAttack.attackerLocation.split(':').map((n) => parseInt(n, 10));
    return calculateTravelTime(province, province.kingdom.isAtWar, tgtIsland, tgtKingdom);
  }, [province, selectedAttack]);

  const daysRemaining = selectedAttack ? Math.max(0, selectedAttack.expiresAtTick - province.sync.currentTick) : null;

  const handleFieldChange = useCallback((field: keyof AmbushComposition, value: number) => {
    setComposition((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSelect = useCallback((attack: AmbushableAttack) => {
    setSelectedAttack(attack);
    setSubmitError(null);
  }, []);

  const hasTroops =
    composition.offenseSpecialists > 0 ||
    composition.elites > 0 ||
    composition.prisoners > 0 ||
    composition.soldiers > 0 ||
    composition.mercenaries > 0;

  const canLaunch = !!selectedAttack && hasTroops && !isSubmitting;

  const handleLaunchClick = useCallback(() => {
    setSubmitError(null);

    if (!selectedAttack) {
      setSubmitError('Select which attack you are ambushing.');
      return;
    }

    const validationError = validateArmyComposition({
      generals: composition.generals,
      soldiers: composition.soldiers,
      includeSoldiers: composition.includeSoldiers,
    });
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setShowConfirmDialog(true);
  }, [selectedAttack, composition.generals, composition.soldiers, composition.includeSoldiers]);

  const handleConfirmAttack = useCallback(async () => {
    setShowConfirmDialog(false);

    if (!provinceId || !worldId || !selectedAttack) {
      setSubmitError('Province ID or World ID not found');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await launchAttack(worldId, provinceId, {
        attackType: 'AMBUSH',
        targetProvinceId: selectedAttack.attackerProvinceId,
        numberOfGenerals: composition.generals,
        numberOfSoldiers: composition.soldiers,
        numberOfOffenseSpecialists: composition.offenseSpecialists,
        numberOfElites: composition.elites,
        numberOfWarHorses: composition.warHorses,
        numberOfPrisoners: composition.prisoners,
        numberOfMercenaries: composition.mercenaries,
        sourceEventSeq: selectedAttack.sourceEventSeq,
      });

      onAttackComplete(result);
      // SF-96: consumption-on-loss feedback — a lost Ambush should read as "opportunity
      // consumed," not a bug, since the opportunity is used up on attempt regardless of outcome.
      addToast(
        result.outcome === 'VICTORY'
          ? `Ambush successful — recaptured ${formatNumber(result.landCaptured)} acres.`
          : 'Ambush failed — the opportunity is consumed regardless of outcome.',
        result.outcome === 'VICTORY' ? 'success' : 'failure'
      );
      setSelectedAttack(null);
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to launch attack';
      setSubmitError(message);
      // SF-96: AMBUSH_NOT_ELIGIBLE means "the list went stale" (async write lag), not a hard
      // error — refresh the list and clear the stale selection rather than leaving a dead pick.
      if (message === AMBUSH_NOT_ELIGIBLE_MESSAGE) {
        setSelectedAttack(null);
        onRefreshTriggerBump();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [provinceId, worldId, selectedAttack, composition, onAttackComplete, addToast, refresh, onRefreshTriggerBump]);

  return (
    <div className="space-y-3">
      <AmbushSourceSelector
        worldId={worldId}
        provinceId={provinceId}
        currentTick={province.sync.currentTick}
        selectedSourceEventSeq={selectedAttack?.sourceEventSeq ?? null}
        onSelect={handleSelect}
        refreshTrigger={refreshTrigger}
      />

      {/* "What you can know" — Ambush's restricted defense formula has no client-exposed value
          today; this is a deliberate scope boundary, not a missing feature (Key Implementation
          Note 3). Deliberately NO Win/Lose badge anywhere in this panel. */}
      <div
        data-testid="ambush-what-you-know"
        className="rounded p-3 space-y-1.5"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
          What you can know
        </div>
        <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
          Ambush uses raw defense points only, ×0.80 (AMBUSH_DEFENSE_EFFICIENCY_MOD) — no
          race/honour/dragon/stance modifiers. There is no reliable client-side outcome preview.
        </div>
        {selectedAttack && (
          <div className="text-xs space-y-0.5 pt-1" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.muted }}>Est. recapture on success</span>
              <span style={{ color: COLORS.text.secondary }}>{formatNumber(selectedAttack.recaptureAmount)} acres</span>
            </div>
            {daysRemaining !== null && (
              <div className="flex justify-between">
                <span style={{ color: COLORS.text.muted }}>Opportunity expires</span>
                <span style={{ color: COLORS.text.secondary }}>in {formatNumber(daysRemaining)} days</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Your OP — computable without target intel, always visible. */}
      <div
        className="rounded p-3"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
            Your OP
          </span>
          <span className="text-lg font-bold" style={{ color: COLORS.positive }}>
            {formatNumber(Math.round(attackOP))}
          </span>
        </div>
      </div>

      {/* Standalone composition form — no MWF solver/oversend (Ambush has neither). */}
      <div
        className="rounded p-3 space-y-3"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
          Your Troops
        </div>

        <div className="space-y-1">
          <label className="text-xs" style={{ color: COLORS.text.tertiary }}>Generals</label>
          <div role="radiogroup" aria-label="Generals" className="flex gap-1.5">
            {[1, 2, 3, 4].map((g) => {
              const isSelected = g === composition.generals;
              return (
                <button
                  key={g}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setComposition((prev) => ({ ...prev, generals: g }))}
                  className="flex-1 rounded text-sm font-semibold"
                  style={{
                    minHeight: 44,
                    border: `1px solid ${isSelected ? COLORS.link : COLORS.border}`,
                    backgroundColor: isSelected ? 'rgba(106, 158, 142, 0.15)' : COLORS.sectionHeader,
                    color: isSelected ? COLORS.link : COLORS.text.secondary,
                  }}
                >
                  {g}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-1.5">
          <AmbushUnitRow label={offspecName} value={composition.offenseSpecialists} max={unitPool.offenseSpecialists} onChange={(v) => handleFieldChange('offenseSpecialists', v)} />
          <AmbushUnitRow label={eliteName} value={composition.elites} max={unitPool.elites} onChange={(v) => handleFieldChange('elites', v)} />
          <AmbushUnitRow label="War Horses" value={composition.warHorses} max={unitPool.warHorses} onChange={(v) => handleFieldChange('warHorses', v)} />
          <AmbushUnitRow label="Prisoners" value={composition.prisoners} max={unitPool.prisoners} onChange={(v) => handleFieldChange('prisoners', v)} />
        </div>

        <div className="space-y-1.5 pt-1" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <label className="flex items-center gap-2 cursor-pointer text-xs" style={{ minHeight: 44, color: COLORS.text.tertiary }}>
            <input
              type="checkbox"
              checked={composition.includeSoldiers}
              onChange={(e) => setComposition((prev) => ({ ...prev, includeSoldiers: e.target.checked, soldiers: 0 }))}
              className="rounded"
            />
            <span>Include soldiers</span>
            <span className="ml-auto" style={{ color: COLORS.text.muted }}>({formatNumber(unitPool.soldiers)} avail)</span>
          </label>
          {composition.includeSoldiers && (
            <AmbushUnitRow label="Soldiers" value={composition.soldiers} max={unitPool.soldiers} onChange={(v) => handleFieldChange('soldiers', v)} warning="Drawing from soldiers reduces your home defense." />
          )}

          <label className="flex items-center gap-2 cursor-pointer text-xs" style={{ minHeight: 44, color: COLORS.text.tertiary }}>
            <input
              type="checkbox"
              checked={composition.includeMercenaries}
              onChange={(e) => setComposition((prev) => ({ ...prev, includeMercenaries: e.target.checked, mercenaries: 0 }))}
              className="rounded"
            />
            <span>Include mercenaries</span>
            <span className="ml-auto text-xs" style={{ color: COLORS.text.muted }}>25 gc/unit</span>
          </label>
          {composition.includeMercenaries && (
            <AmbushUnitRow label="Mercenaries" value={composition.mercenaries} max={10000} onChange={(v) => handleFieldChange('mercenaries', v)} />
          )}
        </div>
      </div>

      {/* Commit bar */}
      <div
        className="rounded px-4 py-3 space-y-2"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        {submitError && (
          <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
        )}
        <div className="text-xs" style={{ color: COLORS.text.muted }}>
          {selectedAttack ? `Army returns in ${travelTime.finalTime} days` : 'Select an attack to ambush above'}
        </div>
        <Button onClick={handleLaunchClick} disabled={!canLaunch} isLoading={isSubmitting} style={{ minHeight: 44 }}>
          Launch Ambush
        </Button>
        {composition.mercenaries > 0 && (
          <div className="text-xs" style={{ color: COLORS.warning }}>
            Mercenary cost: {formatNumber(composition.mercenaries * 25)} gc
          </div>
        )}
      </div>

      <ConfirmAttackDialog
        isOpen={showConfirmDialog}
        onConfirm={handleConfirmAttack}
        onCancel={() => setShowConfirmDialog(false)}
        attackType="AMBUSH"
        targetName={selectedAttack ? `${selectedAttack.attackerProvinceName} (${selectedAttack.attackerLocation})` : ''}
        armyComposition={armyComposition}
        modifiedOffense={attackOP}
        travelTime={travelTime}
        // ConfirmAttackDialog already renders its own unconditional "consumed regardless of
        // outcome" banner for attackType === 'AMBUSH' — noPreviewNote covers the no-preview
        // reasoning only, so the two don't duplicate the same sentence.
        noPreviewNote="No reliable outcome preview — Ambush uses raw defense points only, ×0.80, no race/honour/dragon/stance modifiers."
        mercenaryTotalCost={composition.mercenaries * 25}
        race={race}
      />
    </div>
  );
}

interface AmbushUnitRowProps {
  label: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
  warning?: string;
}

function AmbushUnitRow({ label, value, max, onChange, warning }: AmbushUnitRowProps) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-2">
        <span className="text-xs w-28 shrink-0" style={{ color: COLORS.text.tertiary }}>{label}</span>
        <input
          type="number"
          min={0}
          max={max}
          value={value}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v)) onChange(Math.max(0, Math.min(max, v)));
          }}
          className="w-24 rounded text-sm px-1 py-0.5 text-right"
          style={{ ...INPUT_STYLE, minHeight: 44 }}
        />
        <span className="text-xs" style={{ color: COLORS.text.muted }}>/ {formatNumber(max)}</span>
      </div>
      {warning && (
        <div className="text-xs pl-28" style={{ color: COLORS.warning }}>{warning}</div>
      )}
    </div>
  );
}
